import { draftReturnAtomFamily } from "@babackapp/return-widget-components/atoms/draft-return.atom";
import { useCreateReturnRequest } from "@babackapp/return-widget-components/mutations/use-create-return-request";
import { useDraftReturn } from "@babackapp/return-widget-components/hooks/use-draft-return";
import { useFinancialSummary } from "@babackapp/return-widget-components/hooks/use-financial-summary";
import { usePublicOrder } from "@babackapp/return-widget-components/queries/use-public-order";
import { useSetAtom } from "jotai";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { postToParent } from "../lib/shop-now-embed";
import { trackWidgetEvent } from "../lib/track-widget-event";
import { useWidgetParams } from "./use-widget-params";

export type CompleteStatus = "idle" | "pending" | "success" | "error";

export type UseCompleteReturn = {
  status: CompleteStatus;
  returnRequestId: string | undefined;
  error: Error | null;
  errorCode: string | null;
  confirm: () => void;
};

export function useComplete(): UseCompleteReturn {
  const { orderId, shopId, shopSlug } = useWidgetParams();
  const navigate = useNavigate();
  const draft = useDraftReturn(orderId);
  const setDraft = useSetAtom(draftReturnAtomFamily(orderId));
  const mutation = useCreateReturnRequest(orderId, shopSlug);
  const { data: order } = usePublicOrder(orderId);
  const financialSummary = useFinancialSummary(orderId, order ?? null);

  const confirm = useCallback(() => {
    if (!draft || mutation.status === "pending") return;

    mutation.mutate(draft, {
      onSuccess: (data) => {
        // BBS-237 follow-up: the key signal — did we submit with an amount
        // still due but no captured payment? Backend flags this too
        // (FlagComplementPaymentMissingAtCreationService), but seeing it here
        // as well pinpoints exactly which widget step let it through. Fired
        // only on actual success — a failed mutation created no ReturnRequest,
        // so recording it here too would misrepresent the signal.
        trackWidgetEvent({
          shopId,
          orderId,
          eventType: "return_request_submitted",
          payload: {
            hasIntentId: !!draft.paymentIntentId,
            amountDue: financialSummary.amountDue,
          },
        });
        setDraft(null);
        postToParent({ type: "CLEAR_SHOPIFY_CART" });
        navigate("../return-requests", { replace: true });
        navigate(`../return-requests/${data.id}`);
      },
    });
  }, [
    draft,
    mutation,
    setDraft,
    navigate,
    shopId,
    orderId,
    financialSummary.amountDue,
  ]);

  const errorCode = mutation.error
    ? ((mutation.error as { code?: string }).code ?? "UNKNOWN")
    : null;

  return {
    status: mutation.status === "idle" ? "idle" : mutation.status,
    returnRequestId: mutation.data?.id,
    error: mutation.error,
    errorCode,
    confirm,
  };
}
